/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useLocation, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { CreateTimerV2 } from './components/CreateTimerV2/CreateTimerV2';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { UserDetailsContext } from 'context/userDetailsContext';
import { ModalHeader } from 'shared/ModalHeader';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import { handlePageNumberInPagination } from 'common/pagination';
import { getPageLimit, pickExtractedDate, getFilePathForPagination } from './helpers/timeEntriesHelpersV2';
import { useTimeEntriesListV2Query } from './hooks/queries/useTimeEntriesListV2Query';
import { useDeleteTimeEntryQuery } from './hooks/mutations/useDeleteTimeEntryQuery';
import { updateTimeEntry } from 'services/timerServices';
import { useQueryClient } from '@tanstack/react-query';
import TimeEntriesFiltersV2 from './components/TimeEntriesFiltersV2/TimeEntriesFiltersV2';
import BulkEntriesDialog from 'modules/timer/components/BulkEntriesDialog';
import {
  activityBody as activityBodyHelper,
  billingCodeBody as billingCodeBodyHelper,
  teamMemberBody as teamMemberBodyHelper,
  descriptionBody as descriptionBodyHelper,
  statusBody as statusBodyHelper,
  caseBody as caseBodyHelper,
  durationBody as durationBodyHelper,
  dateBody as dateBodyHelper,
  rateBody as rateBodyHelper,
  amountBody as amountBodyHelper,
  billableBody as billableBodyHelper,
  actionsBody as actionsBodyHelper,
} from './helpers/timeEntriesDataTableHelpersV2';

const TimeEntriesV2 = (props) => {
  const {
    is_automated,
    onTimeManageMent = false,
    caseDetailsPage,
    isLeadSpecific,
    isFromCaseSpecificModule,
    getTimeEntrySummarty,
    onTrustHorizonRefresh,
    caseView,
    caseDetails,
    isFromBilling,
    isActiveTab = true,
  } = props;

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [skToDelete, setSkToDelete] = useState('');
  const [isLead, setIsLead] = useState(false);
  const [editDataObject, setEditDataObject] = useState('');
  const location = useLocation();
  const history = useHistory();
  const { addToast } = useToast();
  const userContext = useContext(UserDetailsContext);
  const timezone = userContext?.userDetails?.timezone;
  const canBulkTime = userContext?.checkSubPermission?.('bulk_time_entries');
  const queryClient = useQueryClient();
  const params = new URLSearchParams(location.search);

  // Filter state
  const [searchKey, setSearchKey] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [selectedBillingStatus, setSelectedBillingStatus] = useState(null);
  const [showUserFilter, setShowUserFilter] = useState(false);
  const [showCreateTimer, setShowCreateTimer] = useState(false);
  const [initialTimeEntryDate, setInitialTimeEntryDate] = useState();
  const [isDateChanged, setIsDateChanged] = useState(false);

  // Pagination and sorting
  const [pageLimit, setPageLimit] = useState(
    getPageLimit(isLeadSpecific, isFromCaseSpecificModule, isFromBilling, onTimeManageMent, is_automated, userContext) ?? 30
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  useEffect(() => {
    setShowCreateTimer(Boolean(location?.state?.createTimeEntry) || params.get('createTimeEntry') === '1');
  }, [location]);

  // Map DataTable field names to API sort_by field names
  const getCurrentSortField = useMemo(() => {
    if (!sortField || sortField === '') return '';
    const fieldMap = {
      module_name: 'module_name', // Activity
      activity_type: 'activity_type', // Billing Code
      'created_by_details.first_name': 'team_member_user_name', // Team Member
      notes: 'notes', // Description
      time_entry_status: 'time_entry_status', // Status
      case_name: 'case_name', // Case
      total_hours: 'total_hours', // Duration
      time_entry_date: 'time_entry_date', // Date
      hour_rate: 'hourly_rate', // Rate (API uses hourly_rate, not hour_rate)
      amount: 'amount', // Amount
      billable: 'billable', // Billable
    };
    return fieldMap[sortField] || '';
  }, [sortField]);

  const getCurrentSortOrder = useMemo(() => {
    if (!sortOrder || sortOrder === '' || sortOrder === null || sortOrder === undefined) return '';
    return sortOrder === 1 ? 'asc' : 'desc';
  }, [sortOrder]);

  // Extract date range from calendar
  const dateRange = useMemo(() => {
    if (!selectedDate || !userContext?.userDetails?.timezone) {
      return {
        time_entry_from: '',
        time_entry_to: '',
      };
    }
    return pickExtractedDate(selectedDate, userContext?.userDetails?.timezone);
  }, [selectedDate, userContext?.userDetails?.timezone]);

  // Prepare query parameters
  const queryParams = useMemo(() => {
    // Map billing status: null or '' (All) -> null, 'billable' -> true, 'nonbillable' -> false
    let billingStatusValue = null;
    if (selectedBillingStatus === 'billable') {
      billingStatusValue = true;
    } else if (selectedBillingStatus === 'nonbillable') {
      billingStatusValue = false;
    } else {
      billingStatusValue = null; // All (null or '')
    }

    // Map invoice status: null (All) -> '', 'INVOICED' -> 'INVOICED', 'OPEN' -> 'OPEN'
    const invoiceStatusValue = selectedStatus === null ? '' : selectedStatus || '';

    return {
      limit: pageLimit,
      page: currentPage,
      billing_status: billingStatusValue,
      invoice_status: invoiceStatusValue,
      time_entry_type: is_automated ? 'automated' : 'manual',
      practice_area: selectedPractice || [],
      time_entry_from: dateRange.time_entry_from || '',
      time_entry_to: dateRange.time_entry_to || '',
      firm_user_id: selectedUser ? (Array.isArray(selectedUser) ? selectedUser : [selectedUser]) : [],
      sort_by: getCurrentSortField,
      sort_order: getCurrentSortOrder,
      search_key: searchKey || '',
      case_id: caseView ? caseDetails?.case_id : selectedCaseId?.id ? selectedCaseId.id : selectedCaseId || '',
    };
  }, [
    pageLimit,
    currentPage,
    selectedStatus,
    selectedBillingStatus,
    is_automated,
    selectedPractice,
    dateRange.time_entry_from,
    dateRange.time_entry_to,
    selectedUser,
    getCurrentSortField,
    getCurrentSortOrder,
    searchKey,
    caseView,
    caseDetails,
    selectedCaseId,
  ]);

  // React Query hook for fetching time entries
  const { timeEntries, totalRecords, totalPages, isLoading, refetch } = useTimeEntriesListV2Query({
    ...queryParams,
    enabled: isActiveTab,
  });

  // Delete time entry mutation
  const deleteTimeEntry = useDeleteTimeEntryQuery({
    onSuccess: () => {
      setShowDeleteConfirmation(false);
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DELETE_TIME_ENTRY_SUCCESS);
      refetch();
      typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
      if (isFromCaseSpecificModule) {
        onTrustHorizonRefresh && onTrustHorizonRefresh();
      }
    },
    onError: (error) => {
      console.error('Failed to delete time entry:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.DELETE_TIME_ENTRY_FAILED);
    },
  });

  // Handlers for filters
  const onChangeSearch = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    setCurrentPage(1);
  };

  const onSearchKeyDown = (e) => {
    const code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      setCurrentPage(1);
    }
  };

  const onChangeCalendar = (e) => {
    setSelectedDate(e?.value);
    setCurrentPage(1);
  };

  const onChangeStatus = (e) => {
    setSelectedStatus(e.value);
    setCurrentPage(1);
  };

  const onChangeCase = (e) => {
    setSelectedCaseId(e?.value);
    setCurrentPage(1);
  };

  const onClearCase = () => {
    setSelectedCaseId('');
    setCurrentPage(1);
  };

  const onChangePractice = (e) => {
    setSelectedPractice([...e.value]);
    setCurrentPage(1);
  };

  const onChangeBillingStatus = (e) => {
    setSelectedBillingStatus(e?.value);
    setCurrentPage(1);
  };

  const onChangeUser = (e) => {
    setSelectedUser(e.value);
    setCurrentPage(1);
  };

  const refreshData = () => {
    setSelectedStatus(null);
    setSearchKey('');
    setSelectedDate(null);
    setSelectedUser('');
    setSelectedPractice(null);
    setSelectedCaseId('');
    setSelectedBillingStatus(null);
    setCurrentPage(1);
    setSortField('');
    setSortOrder('');
  };

  // Pagination handlers
  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const onPageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    setCurrentPage(1);
    const path = getFilePathForPagination(isLeadSpecific, isFromCaseSpecificModule, onTimeManageMent, is_automated);
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, newPageSize);
  };

  // Sort handler
  const onSort = (e) => {
    setSortField(e.sortField || '');
    setSortOrder(e.sortOrder || '');
    setCurrentPage(1);
  };

  // Update billing status handler
  const onUpdateBillingStatus = (e, rowData) => {
    const data = { ...rowData, billable: e.target.checked };
    // Keep the original date - it's already in UTC format from the API
    // No need to convert or update to current time when just toggling billable status
    updateTimeEntry(data, isLeadSpecific || rowData?.is_lead, '', true, false)
      .then(() => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['time-entries', 'list', 'v2'] });
        refetch();
        if (isFromCaseSpecificModule) {
          onTrustHorizonRefresh && onTrustHorizonRefresh();
        }
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.UPDATE_TIME_ENTRY_SUCCESS);
      })
      .catch((err) => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.UPDATE_TIME_ENTRY_FAILED);
      });
  };

  // Edit handler
  const handleEditTimeEntry = (rowData) => {
    setInitialTimeEntryDate(rowData?.time_entry_date);
    // Pass date directly - TimezoneDatePicker handles conversion internally
    // The date from API is already in UTC format, which TimezoneDatePicker expects
    let dataObject = {
      ...rowData,
      // Don't convert here - TimezoneDatePicker expects UTC format from API
    };
    setEditDataObject(dataObject);
    setShowEditDialog(true);
  };

  // Delete handler
  const handleDeleteTimeEntry = (rowData) => {
    let formattedSk = rowData.sk ? rowData.sk.replaceAll('#', '%23') : '';
    setSkToDelete(formattedSk);
    setIsLead(rowData?.is_lead);
    setShowDeleteConfirmation(true);
  };

  // Confirm delete
  const confirmDelete = () => {
    if (skToDelete) {
      deleteTimeEntry.mutate({
        time_entry_sk: skToDelete,
        is_lead: isLeadSpecific ? isLeadSpecific : isLead,
      });
    }
  };

  // Hide create timer modal
  const hideCreateTimer = () => {
    setShowCreateTimer(false);
    setShowEditDialog(false);
    setIsDateChanged(false);
    const params = new URLSearchParams(location.search);
    params.set('createTimeEntry', '0');
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  // Create timer success handler
  const createTimerSuccess = () => {
    hideCreateTimer();
    queryClient.invalidateQueries({ queryKey: ['time-entries', 'list', 'v2'] });
    queryClient.invalidateQueries({ queryKey: ['billing', 'summary'] });
    refetch();
    if (isFromCaseSpecificModule) {
      onTrustHorizonRefresh && onTrustHorizonRefresh();
    }
    typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
  };

  // Effect to handle body overflow for modals
  useEffect(() => {
    if (showCreateTimer || showEditDialog) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showCreateTimer, showEditDialog]);

  // Table body renderers using helpers
  const activityBody = (rowData) => activityBodyHelper({ rowData });
  const billingCodeBody = (rowData) => billingCodeBodyHelper({ rowData });
  const teamMemberBody = (rowData) => teamMemberBodyHelper({ rowData });
  const descriptionBody = (rowData) => descriptionBodyHelper({ rowData });
  const statusBody = (rowData) => statusBodyHelper({ rowData });
  const caseBody = (rowData) => caseBodyHelper({ rowData, is_automated });
  const durationBody = (rowData) => durationBodyHelper({ rowData });
  const dateBody = (rowData) => dateBodyHelper({ rowData, timezone: userContext?.userDetails?.timezone });
  const rateBody = (rowData) => rateBodyHelper({ rowData });
  const amountBody = (rowData) => amountBodyHelper({ rowData });
  const billableBody = (rowData) => billableBodyHelper({ rowData, onUpdateBillingStatus });
  const actionsBody = (rowData) =>
    actionsBodyHelper({
      rowData,
      onEdit: handleEditTimeEntry,
      onDelete: handleDeleteTimeEntry,
    });

  return (
    <div className="container-fluid mx-0 p-0">
      <GlobalLoader />
      {!is_automated && (
        <div className="d-flex align-items-center mb-3">
          <div className="ms-auto d-flex gap-2">
            {canBulkTime ? (
              <Button
                label="Add Bulk Time Entries"
                icon="pi pi-plus"
                className="p-button p-button-primary"
                onClick={() => setShowBulkDialog(true)}
              />
            ) : null}
            <Button
              label="Add Time Entry"
              icon="pi pi-plus"
              className="p-button p-button-primary"
              onClick={() => {
                setShowCreateTimer(true);
                setEditDataObject('');
              }}
            />
          </div>
        </div>
      )}
      <TimeEntriesFiltersV2
        searchKey={searchKey}
        onChangeSearch={onChangeSearch}
        onSearchKeyDown={onSearchKeyDown}
        selectedDate={selectedDate}
        onChangeCalendar={onChangeCalendar}
        selectedStatus={selectedStatus}
        onChangeStatus={onChangeStatus}
        selectedCaseId={selectedCaseId}
        onChangeCase={onChangeCase}
        onClearCase={onClearCase}
        selectedPractice={selectedPractice}
        onChangePractice={onChangePractice}
        selectedBillingStatus={selectedBillingStatus}
        onChangeBillingStatus={onChangeBillingStatus}
        selectedUser={selectedUser}
        onChangeUser={onChangeUser}
        showUserFilter={showUserFilter}
        onShowUserFilter={() => setShowUserFilter(true)}
        refreshData={refreshData}
        is_automated={is_automated}
        caseDetailsPage={caseDetailsPage}
        isFromCaseSpecificModule={isFromCaseSpecificModule}
        isLeadSpecific={isLeadSpecific}
      />

      <BulkEntriesDialog
        visible={showBulkDialog}
        onHide={() => setShowBulkDialog(false)}
        userTimezone={timezone}
        caseFullList={[]}
        searchCases={() => {}}
        isAutomated={is_automated}
        caseView={caseView}
        caseDetails={caseDetails}
        isLeadSpecific={isLeadSpecific}
        onSaved={() => {
          refetch();
          if (isFromCaseSpecificModule) {
            onTrustHorizonRefresh && onTrustHorizonRefresh();
          }
          typeof getTimeEntrySummarty === 'function' && getTimeEntrySummarty();
        }}
      />

      <ConfirmDialog
        className="common-popup-style confirm-delete-popup"
        message="Are you sure you want to delete?"
        header="Confirm Delete"
        rejectClassName="p-button-secondary outline"
        icon="pi pi-exclamation-triangle"
        visible={showDeleteConfirmation}
        accept={confirmDelete}
        reject={() => {
          setShowDeleteConfirmation(false);
          setSkToDelete('');
        }}
        onHide={() => {
          setShowDeleteConfirmation(false);
          setSkToDelete('');
        }}
      />

      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={timeEntries || []}
          responsiveLayout="scroll"
          className="p-datatable-responsive"
          emptyMessage={isLoading ? 'Loading...' : 'No Data Found.'}
          paginator={false}
          rows={pageLimit ?? 30}
          stripedRows
          sortMode="server"
          sortField={sortField || null}
          sortOrder={sortOrder || null}
          onSort={onSort}
          storageKey={is_automated ? 'automated_time_entries_column_widths' : 'time_entries_column_widths'}
          columns={[
            {
              field: 'module_name',
              header: 'Activity',
              body: activityBody,
              sortable: true,
              bodyClassName: 'ellipsis-text capitalize-text',
              hidden: !is_automated,
            },
            {
              field: 'activity_type',
              header: 'Billing Code',
              body: billingCodeBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            {
              field: 'created_by_details.first_name',
              header: 'Team Member',
              body: teamMemberBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            {
              field: 'notes',
              header: 'Description',
              body: descriptionBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            {
              field: 'time_entry_status',
              header: 'Status',
              body: statusBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            isFromBilling && {
              field: 'case_name',
              header: is_automated ? 'Case' : 'Case/Lead',
              body: caseBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            {
              field: 'total_hours',
              header: 'Duration',
              body: durationBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            {
              field: 'time_entry_date',
              header: 'Date',
              body: dateBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            {
              field: 'hour_rate',
              header: 'Rate',
              body: rateBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            {
              field: 'amount',
              header: 'Amount',
              body: amountBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            {
              field: 'billable',
              header: 'Billable',
              body: billableBody,
              sortable: true,
              bodyClassName: 'ellipsis-text',
            },
            {
              field: 'action_icons',
              header: '',
              body: actionsBody,
              bodyClassName: 'ellipsis-text',
            },
          ].filter((val) => val)}
        />

        <EllipsisPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={totalRecords}
          pageSize={pageLimit}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showPageSizeDropdown={true}
        />
      </div>

      {showCreateTimer || showEditDialog ? (
        <Dialog
          className="add-contact-dialog create-msg-popup"
          visible={showCreateTimer || showEditDialog}
          onHide={hideCreateTimer}
          style={{ width: '40vw' }}
          closable={false}
          header={ModalHeader}
        >
          <CreateTimerV2
            header={`${showEditDialog ? 'Edit ' : 'Add '}Time Entry`}
            isFromTimeManger={true}
            is_automated={is_automated}
            createTimerSuccess={createTimerSuccess}
            cancelCreateTimer={hideCreateTimer}
            selectedTimer={editDataObject}
            caseView={caseView}
            caseDetails={caseDetails}
            isLeadSpecific={isLeadSpecific}
            initialTimeEntryDate={initialTimeEntryDate}
            isDateChanged={isDateChanged}
            setIsDateChanged={setIsDateChanged}
          />
        </Dialog>
      ) : null}
    </div>
  );
};

export default TimeEntriesV2;
